import { Component, inject, input, signal, computed, ViewChild, ElementRef, DestroyRef, effect } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BtnLoaderDirective, rxResourceFixed } from '@zayan-erp/shared';
import { of } from 'rxjs';
import { SalesApsService } from '../../../services/sales-aps.service';
import { DealIncentive } from '../../../models/incentive.models';
import {
  AbilityService,
  CurrencyService,
  SvgIconsComponent,
  NgbModalService,
  CurrencyFormatPipe,
  CurrencyInputFieldComponent
} from '@zayan-erp/shared';

@Component({
  selector: 'deal-incentives',
  imports: [
    ReactiveFormsModule,
    SvgIconsComponent,
    CurrencyFormatPipe,
    CurrencyInputFieldComponent,
    BtnLoaderDirective
  ],
  templateUrl: './deal-incentives.component.html'
})
export class DealIncentivesComponent {
  @ViewChild('incentiveModal') incentiveModal!: ElementRef;

  // Input
  readonly dealId = input.required<string>();

  // Services
  readonly #fb = inject(FormBuilder);
  readonly #salesApsService = inject(SalesApsService);
  readonly #currencyService = inject(CurrencyService);
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #currencyPipe = new CurrencyPipe('en-US');
  readonly #destroyRef = inject(DestroyRef);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  // State
  readonly isEditMode = signal<boolean>(false);
  readonly totalAmount = signal<number>(0);

  // Data loading
  readonly incentiveResource = rxResourceFixed<DealIncentive | null, { dealId: string }>({
    params: () => ({ dealId: this.#salesApsService.currentDeal()?.id ?? '' }),
    defaultValue: null,
    stream: ({ params }) => (params.dealId ? this.#salesApsService.getDealIncentive(params.dealId) : of(null))
  });

  readonly incentive = computed(() => this.incentiveResource.value());
  readonly hasIncentive = computed(() => !!this.incentive());

  // Form
  readonly incentiveForm = this.#fb.group(
    {
      lotPrice: this.#fb.control<number | null>({ value: null, disabled: true }, [Validators.min(0)]),
      lotPremium: this.#fb.control<number | null>({ value: null, disabled: true }, [Validators.min(0)]),
      baseModel: this.#fb.control<number | null>({ value: null, disabled: true }, [Validators.min(0)]),
      addendumProducts: [null as number | null, [Validators.min(0)]]
    },
    { validators: this.#atLeastOneFieldRequired() }
  );

  constructor() {
    this.#setupFormTotalCalculation();
  }

  #setupFormTotalCalculation(): void {
    this.incentiveForm.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(values => {
      const total =
        (values.lotPrice || 0) + (values.lotPremium || 0) + (values.baseModel || 0) + (values.addendumProducts || 0);
      this.totalAmount.set(total);
    });
  }

  openIncentiveModal(): void {
    if (!this.canWriteDeals()) return;

    const existingIncentive = this.incentive();

    if (existingIncentive) {
      this.#openEditMode(existingIncentive);
    } else {
      this.#openCreateMode();
    }

    this.#modalService.open(this.incentiveModal, { ...this.#ngbModalService.centerModal, size: 'lg' });
  }

  #openEditMode(incentive: DealIncentive): void {
    this.isEditMode.set(true);
    this.incentiveForm.patchValue({
      lotPrice: incentive.lotPrice,
      lotPremium: incentive.lotPremium,
      baseModel: incentive.baseModel,
      addendumProducts: incentive.addendumProducts
    });
    this.totalAmount.set(this.#calculateTotal(incentive));
  }

  #openCreateMode(): void {
    this.isEditMode.set(false);
    this.incentiveForm.reset({ lotPrice: null, lotPremium: null, baseModel: null, addendumProducts: null });
    this.totalAmount.set(0);
  }

  submitIncentiveForm(): void {
    if (!this.canWriteDeals()) return;
    if (this.incentiveForm.invalid) {
      this.incentiveForm.markAllAsTouched();
      return;
    }

    const payload = this.#buildPayload();
    const existingIncentive = this.incentive();

    const saveOperation$ = existingIncentive?.id
      ? this.#salesApsService.updateDealIncentive(existingIncentive.id, payload)
      : this.#salesApsService.createDealIncentive(payload);

    saveOperation$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: () => this.#handleSaveSuccess(),
      error: error => console.error('Failed to save incentive:', error)
    });
  }

  #buildPayload() {
    const formValues = this.incentiveForm.getRawValue();
    const dealId = this.dealId() || this.#salesApsService.currentDeal()?.id || '';
    return {
      dealId,
      lotPrice: formValues.lotPrice || 0,
      lotPremium: formValues.lotPremium || 0,
      baseModel: formValues.baseModel || 0,
      addendumProducts: formValues.addendumProducts || 0
    };
  }

  #handleSaveSuccess(): void {
    this.#modalService.dismissAll();
    this.incentiveResource.reload();
  }

  deleteIncentive(): void {
    if (!this.canWriteDeals()) return;

    const incentive = this.incentive();
    if (!incentive?.id || !confirm('Are you sure you want to delete this incentive?')) {
      return;
    }

    this.#salesApsService
      .deleteDealIncentive(incentive.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.incentiveResource.reload(),
        error: error => console.error('Failed to delete incentive:', error)
      });
  }

  formatCurrency(value: number): string {
    const code = this.#currencyService.currencyCode();
    return this.#currencyPipe.transform(value, code, 'symbol', '1.2-2') || '';
  }

  #calculateTotal(incentive: DealIncentive): number {
    return (
      (incentive.lotPrice || 0) +
      (incentive.lotPremium || 0) +
      (incentive.baseModel || 0) +
      (incentive.addendumProducts || 0)
    );
  }

  #atLeastOneFieldRequired() {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control as any;
      const values = ['lotPrice', 'lotPremium', 'baseModel', 'addendumProducts'].map(
        field => formGroup.get(field)?.value
      );

      const hasAtLeastOneValue = values.some(value => value !== null && value !== undefined && value !== '');

      return hasAtLeastOneValue ? null : { atLeastOneRequired: true };
    };
  }
}
